import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import MainContainer from "../UI/MainContainer";
import axios from "../../store/api/axios";
import AudioEditor from "./AudioEditor";
import { PiHeart, PiHeartFill, PiCheckBold } from "react-icons/pi";
import { CiEdit, CiTrash, CiSaveDown2, CiPlay1, CiPause1 } from "react-icons/ci";
import { BsArrowLeft } from "react-icons/bs";
import { IoEllipsisHorizontal, IoAlertCircleOutline, IoClose, IoOpenOutline, IoAdd } from "react-icons/io5";
import LastConfirmWarningModal from "../UI/LastConfirmWarningModal";
import { AudioFile, AudioStatus, Comment, Post, Section } from "../../types/Types";
import Button from "../UI/Button";
import { ButtonTypes, DEFAULT_AUTH_DATA } from "../../utils/Constants";
import FileDropzone from "../UI/FileDropzone";
import { mixAndDownload } from "../../utils/mixAndDownload";
import { formatDMY, formatDMYHM, formatTimeAgo } from "../../utils/DateUtils";
import { isAudioFile } from "../../utils/fileTypes";
import CommentSection from "../UI/CommentSection";
import CircularLoading from "../UI/CircularLoading";
import AudioPlayer from "../UI/AudioPlayer";
import { toast } from "../../utils/toast";
import usePageTitle from "../../utils/usePageTitle";

interface Track {
  id: string;
  musicianId: string;
  src: string;
  audioRef: React.RefObject<HTMLAudioElement>;
}

// MediaRecorder has no "record as WAV" option, so recording used to lie
// about the format (labeling whatever the browser actually produced as
// "audio/wav"/"recorded-audio.wav"), which the server would then store and
// serve back with a mismatched Content-Type — breaking playback/processing.
// Picking (and honestly naming) a real supported format fixes that.
const RECORDING_MIME_CANDIDATES = [
  { mimeType: "audio/webm;codecs=opus", extension: "webm" },
  { mimeType: "audio/webm", extension: "webm" },
  { mimeType: "audio/ogg;codecs=opus", extension: "ogg" },
  { mimeType: "audio/ogg", extension: "ogg" },
  { mimeType: "audio/mp4", extension: "m4a" },
];

const pickSupportedRecordingMimeType = (): string => {
  if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported) {
    for (const candidate of RECORDING_MIME_CANDIDATES) {
      if (MediaRecorder.isTypeSupported(candidate.mimeType)) {
        return candidate.mimeType;
      }
    }
  }
  return "";
};

const extensionForMimeType = (mimeType: string): string => {
  if (mimeType.includes("webm")) return "webm";
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("wav")) return "wav";
  if (mimeType.includes("mp4") || mimeType.includes("aac")) return "m4a";
  if (mimeType.includes("mpeg") || mimeType.includes("mp3")) return "mp3";
  return "webm";
};

const PostPage: React.FC = () => {
  const authData = localStorage.getItem("auth_data") ? JSON.parse(localStorage.getItem("auth_data")!) : DEFAULT_AUTH_DATA;
  const { postId } = useParams<{ postId: string }>();
  const [post, setPost] = useState<Post | null>(null);
  usePageTitle(post?.title);
  const [tracks, setTracks] = useState<Track[]>();
  const [playingTracks, setPlayingTracks] = useState<string[]>([]);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const mainAudioRef = useRef<HTMLAudioElement>(null);
  const navigate = useNavigate();
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);
const [isLoading, setIsLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | null>(null);
  const audioContext = useRef<AudioContext | null>(null);
  const editedWaveSurferRef = useRef<any>(null);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [time, setTime] = useState(0); // Time in milliseconds
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteAudioModal, setShowDeleteAudioModal] = useState(false);
  const [audioIdDelete, setAudioIdDelete] = useState<string>("");
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isMixing, setIsMixing] = useState(false);
  const [isModerating, setIsModerating] = useState(false);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [comments, setComments] = useState<Comment[]>([]);
  const [isSendingComment, setIsSendingComment] = useState(false);
  const [editingAudio, setEditingAudio] = useState<AudioFile | null>(null);
  const [editedBlob, setEditedBlob] = useState<File | null>(null);
  const [editingFile, setEditingFile] = useState<File | null>(null);
  const isOwner = post != null && authData?.id != null && String(post.user) === String(authData.id);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [deletingAudioId, setDeletingAudioId] = useState<string | null>(null);
  const [savingEditedId, setSavingEditedId] = useState<string | null>(null);
  // Edit downloads the whole audio before the editor opens, which can take a
  // few seconds. While it does, the Edit buttons show a spinner and ignore
  // clicks (the ref also catches a double click before the re-render).
  const [openingEditId, setOpeningEditId] = useState<string | null>(null);
  const openingEditRef = useRef(false);
  const [monitorMainWhileRecording, setMonitorMainWhileRecording] = useState(true);
  const [countdown, setCountdown] = useState<number | null>(null);
  const recordingStartRef = useRef<number | null>(null);
  // Was a fixed 5 seconds; now 3 by default, with Off and 5 s as options.
  const [countInSeconds, setCountInSeconds] = useState<0 | 3 | 5>(3);
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [micId, setMicId] = useState("");
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const [inputLevel, setInputLevel] = useState(0);
  const levelMeterRef = useRef<{ ctx: AudioContext; raf: number; lastUpdate: number } | null>(null);
  const [ownerMenuOpen, setOwnerMenuOpen] = useState(false);
  const ownerMenuRef = useRef<HTMLDivElement>(null);
  const [takeFilter, setTakeFilter] = useState<"all" | "pending" | "approved">("all");
  const [isMainPlaying, setIsMainPlaying] = useState(false);

  const statusLabel = (s: AudioStatus) =>
    s === 'PD' ? 'Pending' : s === 'AP' ? 'Approved' : 'Rejected';

  const statusBadgeClass = (s: AudioStatus) =>
    s === 'PD'
      ? 'bg-orange-primary33 text-[#8a4f00]'
      : s === 'AP'
        ? 'bg-green-primary33 text-[#03644a]'
        : 'bg-red-primary/10 text-red-primary';

  useEffect(() => {
    if (!audioContext.current) {
      audioContext.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const fetchPostData = async () => {
      if (postId) {
        try {
          const response = await axios.get(`/posts/${postId}/`);
          const postData = response.data;
          setPost(postData);

          const tracks = postData.sections.flatMap((section: Section) =>
            section.audio_files?.map((audio: AudioFile) => ({
              id: String(audio.id),
              user: audio.user,
              musician: `${audio.user_info.first_name} ${audio.user_info.last_name} (${audio.user_info.username})`,
              src: audio.audio_file,
              audioRef: React.createRef<HTMLAudioElement>(),
            }))
          );
          setTracks(tracks);
          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          console.error("Error fetching post data:", error);
        }
      }
    };

    fetchPostData();
  }, [postId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (mainAudioRef.current && !mainAudioRef.current.paused) {
        setCurrentTime(mainAudioRef.current.currentTime);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  function waitUntilReady(el: HTMLAudioElement) {
    return el.readyState >= 2
      ? Promise.resolve()
      : new Promise<void>((resolve) => {
        const onReady = () => { el.removeEventListener('loadedmetadata', onReady); el.removeEventListener('canplay', onReady); resolve(); };
        el.addEventListener('loadedmetadata', onReady, { once: true });
        el.addEventListener('canplay', onReady, { once: true });
      });
  }

  const handleCheckboxChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    trackId: string
  ) => {
    const audioRef = tracks?.find(t => t.id === trackId)?.audioRef.current;
    if (!audioRef) return;

    // if (event.target.checked) {
    //   await waitUntilReady(audioRef);
    //   const t = mainAudioRef.current?.currentTime ?? 0;
    //   audioRef.currentTime = t;
    //   try { await audioRef.play(); } catch { }
    //   setPlayingTracks(prev => [...prev, trackId]);
    // }
    if (event.target.checked) {
      setPlayingTracks(prev => [...prev, trackId]);
      if (mainAudioRef.current && !mainAudioRef.current.paused) {
        await waitUntilReady(audioRef);
        const t = mainAudioRef.current?.currentTime ?? 0;
        audioRef.currentTime = t;
        try { await audioRef.play(); } catch { }
      }
    } else {
      audioRef.pause();
      audioRef.currentTime = 0;
      setPlayingTracks(prev => prev.filter(id => id !== trackId));
    }
  };

  const handleMainAudioPlay = () => {
    const mainAudio = mainAudioRef.current;
    if (mainAudio) {
      (tracks ?? []).forEach((track) => {
        const audio = track.audioRef.current;
        if (audio && playingTracks.includes(track.id)) {
          audio.currentTime = mainAudio.currentTime;
          audio.play();
        }
      });
    }
  };

  // Audios "in the mix" follow the main track: they pause with it (they
  // used to keep playing on their own) and jump with it when it's seeked.
  const handleMainAudioPause = () => {
    (tracks ?? []).forEach((track) => {
      const audio = track.audioRef.current;
      if (audio && playingTracks.includes(track.id) && !audio.paused) audio.pause();
    });
  };

  const resyncAudiosToMain = () => {
    const mainAudio = mainAudioRef.current;
    if (!mainAudio || mainAudio.paused) return;
    (tracks ?? []).forEach((track) => {
      const audio = track.audioRef.current;
      if (audio && playingTracks.includes(track.id)) audio.currentTime = mainAudio.currentTime;
    });
  };

  const toggleMainPlayback = () => {
    const mainAudio = mainAudioRef.current;
    if (!mainAudio) return;
    if (mainAudio.paused) mainAudio.play().catch(() => { });
    else mainAudio.pause();
  };

  const pauseMainTrack = () => {
    mainAudioRef.current?.pause();
  };

  const handleAddAudio = async (audio: File | null, sectionId: string) => {
    if (!audio) return;

    const formData = new FormData();
    formData.append('audio_file', audio);
    formData.append('user', authData.id);
    formData.append('section', sectionId);

    try {
      setIsUploadingAudio(true);
      const response = await axios.post(`/sections/${sectionId}/audio/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const newAudio = response.data;
      const newTrack = {
        id: String(newAudio.id),
        musicianId: newAudio.user_info?.username ?? newAudio.user,
        src: newAudio.audio_file,
        audioRef: React.createRef<HTMLAudioElement>(),
      };

      setTracks((prevTracks) => [...(prevTracks ?? []), newTrack]);

      const updatedSections = post!.sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            audio_files: [...(section.audio_files ?? []), newAudio]
          };
        }
        return section;
      });

      setPost((prev) => (prev ? { ...prev, sections: updatedSections } : null));
      setCurrentSectionId(null);
      setSelectedFile(null);
      setTime(0);

    } catch (error) {
      console.error("Error uploading audio file:", error);
    } finally {
      setIsUploadingAudio(false);
      // navigate(0);
      // window.location.reload();
    }
  };

  const archivePostHandler = async () => {
    if (!postId || !post) return;

    try {
      setIsArchiving(true);
      await axios.patch(`/posts/${postId}/`, { is_archived: !post.is_archived });

      const { data } = await axios.get(`/posts/${postId}/`); //???? نباید دوباره api call کنه
      const normalized: Post = {
        ...data,
        created_at: data.created_at ? new Date(data.created_at) : null,
        archived_at: data.archived_at ? new Date(data.archived_at) : null,
      };
      setPost(normalized);
    } catch (err) {
      console.error("Failed to toggle archive:", err);
      toast.error("Could not update archive status.");
    } finally {
      setIsArchiving(false);
    }
  };

  const deletePostHandler = async () => {
    if (!postId) return;

    try {
      await axios.delete(`/posts/${postId}/`);
      navigate(-1);
    } catch (err: any) {
      console.error(err);
    }
  };

  // Same steps as the old file-input handler, but takes the file directly so
  // dropping and browsing (FileDropzone) go through the same path.
  const selectTakeFile = async (file: File) => {
    setSelectedFile(file);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const audioCtx = new AudioContext();
      audioContext.current = audioCtx;
      const decodedAudio = await audioCtx.decodeAudioData(arrayBuffer);
      setAudioBuffer(decodedAudio);
    } catch (error) {
      console.error("Error decoding audio:", error);
    }
  };

  const refreshMicDevices = async () => {
    try {
      const devices = await navigator.mediaDevices?.enumerateDevices?.();
      setMicDevices((devices ?? []).filter((d) => d.kind === "audioinput"));
    } catch (error) {
      console.error("Could not list microphones:", error);
    }
  };

  // Live input level while recording, from the same stream the recorder
  // uses. Updates ~15 times a second so the page doesn't re-render per frame.
  const startLevelMeter = (stream: MediaStream) => {
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new Ctx();
      // Some browsers (e.g. Safari) create it suspended when it isn't made
      // directly inside the click.
      ctx.resume?.().catch(() => { });
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const data = new Float32Array(analyser.fftSize);
      const meter = { ctx, raf: 0, lastUpdate: 0 };
      const tick = (now: number) => {
        analyser.getFloatTimeDomainData(data);
        if (now - meter.lastUpdate > 66) {
          let peak = 0;
          for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i]));
          setInputLevel(Math.min(1, peak));
          meter.lastUpdate = now;
        }
        meter.raf = requestAnimationFrame(tick);
      };
      meter.raf = requestAnimationFrame(tick);
      levelMeterRef.current = meter;
    } catch (error) {
      console.error("Input level meter unavailable:", error);
    }
  };

  const stopLevelMeter = () => {
    const meter = levelMeterRef.current;
    if (!meter) return;
    cancelAnimationFrame(meter.raf);
    meter.ctx.close().catch(() => { });
    levelMeterRef.current = null;
    setInputLevel(0);
  };

  const stopRecordingStream = () => {
    recordingStreamRef.current?.getTracks().forEach((t) => t.stop());
    recordingStreamRef.current = null;
  };

  // Leaving the page mid-recording shouldn't leave the microphone on.
  useEffect(() => {
    return () => {
      stopLevelMeter();
      stopRecordingStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentSectionId) refreshMicDevices();
  }, [currentSectionId]);

  useEffect(() => {
    if (!ownerMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (ownerMenuRef.current && !ownerMenuRef.current.contains(e.target as Node)) setOwnerMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [ownerMenuOpen]);

  const handlePlayTogether = () => {
    if (mainAudioRef.current && editedWaveSurferRef.current) {
      const currentTime = mainAudioRef.current.currentTime;
      mainAudioRef.current.currentTime = currentTime;
      editedWaveSurferRef.current.seekTo(currentTime / editedWaveSurferRef.current.getDuration());

      mainAudioRef.current.play();
      editedWaveSurferRef.current.play();
    }
  };

  const resetPlayingTogether = () => {
    if (mainAudioRef.current) {
      mainAudioRef.current.pause();
      mainAudioRef.current.currentTime = 0;
    }

    if (editedWaveSurferRef.current) {
      editedWaveSurferRef.current.pause();
      editedWaveSurferRef.current.seekTo(0);
    }
  };

  const startRecordingOld = () => {
    if (navigator.mediaDevices) {
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        mediaRecorderRef.current = new MediaRecorder(stream);
        mediaRecorderRef.current.ondataavailable = (event) => {
          audioChunksRef.current.push(event.data);
        };
        mediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
          // const audioUrl = URL.createObjectURL(audioBlob);
          // setRecordedAudioUrl(audioUrl);
          setSelectedFile(new File([audioBlob], 'recorded-audio.wav'));
          audioChunksRef.current = [];
        };
        mediaRecorderRef.current.start();
        setTime(0);
        setIsRecording(true);
      });
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices) return;

    try {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(
          micId ? { audio: { deviceId: { exact: micId } } } : { audio: true }
        );
      } catch (err) {
        // The chosen microphone may have been unplugged; fall back to the
        // default one instead of not recording at all.
        if (!micId) throw err;
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setMicId("");
        toast.error("That microphone isn't available.", "Recording with the default microphone instead.");
      }
      recordingStreamRef.current = stream;
      startLevelMeter(stream);
      // Device names are only visible after permission is granted.
      refreshMicDevices();

      recordingStartRef.current = Date.now();

      // ⭐ START MAIN AUDIO IF CHECKED
      if (monitorMainWhileRecording && mainAudioRef.current) {
        mainAudioRef.current.currentTime = 0; // optional
        await mainAudioRef.current.play().catch(() => { });
      }

      const preferredMimeType = pickSupportedRecordingMimeType();
      mediaRecorderRef.current = new MediaRecorder(
        stream,
        preferredMimeType ? { mimeType: preferredMimeType } : undefined
      );

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        // Use whatever MediaRecorder actually produced, not a guessed/false label.
        const actualMimeType = mediaRecorderRef.current?.mimeType || preferredMimeType || "audio/webm";
        const extension = extensionForMimeType(actualMimeType);
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        setSelectedFile(new File([audioBlob], `recorded-audio.${extension}`, { type: actualMimeType }));
        audioChunksRef.current = [];
        // Release the microphone (the browser's mic indicator used to stay
        // on after recording, because the stream was never stopped).
        stopRecordingStream();
      };

      mediaRecorderRef.current.start();
      setTime(0);
      setIsRecording(true);

    } catch (err) {
      console.error("Mic access denied:", err);
      stopLevelMeter();
      stopRecordingStream();
      toast.error("Couldn't start recording.", "Allow microphone access in your browser and try again.");
    }
  };

  const startRecordingWithCountdown = () => {
    if (isRecording || countdown !== null) return;

    if (countInSeconds === 0) {
      startRecording();
      return;
    }

    let count: number = countInSeconds;
    setCountdown(count);

    const interval = setInterval(() => {
      count -= 1;

      if (count === 0) {
        clearInterval(interval);
        setCountdown(null);
        startRecording(); // 🔥 call your original recording function
      } else {
        setCountdown(count);
      }
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      stopLevelMeter();

      if (monitorMainWhileRecording && mainAudioRef.current) {
        mainAudioRef.current.pause();
        mainAudioRef.current.currentTime = 0;
      }
    }
  };

  useEffect(() => {
    // Drives the on-screen recording timer (formatTime expects milliseconds).
    // This used to be dead: `time` was reset to 0 on start/stop but nothing
    // ever incremented it, so the timer stayed at 0:00 for the whole recording.
    if (!isRecording) return;

    const id = setInterval(() => {
      setTime((t) => t + 100);
    }, 100);

    return () => clearInterval(id);
  }, [isRecording]);

  useEffect(() => {
    if (!isRecording) return;

    const audio = mainAudioRef.current;
    const startTime = recordingStartRef.current;

    if (!audio || startTime == null) return;

    if (!monitorMainWhileRecording) {
      audio.pause();
      return;
    }

    const elapsedSeconds =
      (Date.now() - startTime) / 1000;

    // prevent jumping beyond duration
    if (!isNaN(audio.duration)) {
      audio.currentTime = Math.min(elapsedSeconds, audio.duration);
    } else {
      audio.currentTime = elapsedSeconds;
    }

    audio.play().catch(() => { });
  }, [monitorMainWhileRecording, isRecording]);

  // "1:05.3" (was rendered via dangerouslySetInnerHTML; plain text now).
  const formatRecordingTime = (time: number) => {
    const minutes = Math.floor(time / 60000);
    const seconds = Math.floor((time % 60000) / 1000);
    const tenths = Math.floor((time % 1000) / 100);
    return `${minutes}:${seconds < 10 ? "0" + seconds : seconds}.${tenths}`;
  };

  // Back to step 1 with a clean recorder: the timer used to keep showing the
  // previous recording's length instead of 0:00.
  const backToRecordOrUpload = () => {
    setSelectedFile(null);
    setTime(0);
  };

  // Closing the "Add audio" panel stops a recording in progress and
  // forgets the chosen audio, so reopening starts clean.
  const closeAddPart = () => {
    if (isRecording) stopRecording();
    setCurrentSectionId(null);
    backToRecordOrUpload();
  };

  const openAddPart = (sectionId: string) => {
    if (currentSectionId === sectionId) return;
    if (isRecording) stopRecording();
    backToRecordOrUpload();
    setCurrentSectionId(sectionId);
  };

  const handleDownloadOutputFrontend = async () => {
    if (!post) return;

    const selectedTrackUrls =
      (tracks ?? [])
        .filter((t) => playingTracks.includes(t.id))
        .map((t) => t.src);

    const urls = [
      ...(post.mp3_file ? [post.mp3_file] : []),
      ...selectedTrackUrls,
    ];

    if (urls.length === 0) {
      toast.error("Select at least one audio (including the main track) to mix.");
      return;
    }

    try {
      setIsMixing(true);
      const safeTitle = (post.title || "mix").replace(/[^\w\-]+/g, "_");
      await mixAndDownload(urls, `${safeTitle}.wav`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to render audio. Check console for details.");
    } finally {
      setIsMixing(false);
    }
  };

  const handleDownloadOutput1 = async () => {
    if (!postId) return;

    let selectedAudioIds = (tracks ?? [])
      .filter((t) => playingTracks.includes(t.id))
      .map((t) => t.id);

    if (post?.main_audio_id) {
      if (!selectedAudioIds.includes(post.main_audio_id)) {
        selectedAudioIds.unshift(post.main_audio_id);
      }
    }

    if (selectedAudioIds.length === 0) {
      toast.error("No audio available to download.");
      return;
    }

    try {
      setIsMixing(true);
      const res = await axios.post(
        `/mixed-download/${postId}/`,
        { audio_ids: selectedAudioIds },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `mix_post_${postId}.mp3`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error(e);
      toast.error("Download failed");
    }
    finally {
      setIsMixing(false);
    }
  };

  const handleDownloadOutput = async () => {
    if (!postId) return;

    const selectedAudioIds = (tracks ?? [])
      .filter(t => playingTracks.includes(t.id))
      .map(t => t.id);

    // Allow "main-only" download when nothing is selected
    if (selectedAudioIds.length === 0 && !post?.mp3_file) {
      toast.error("No audio available to download.");
      return;
    }

    try {
      setIsMixing(true);
      const res = await axios.post(
        `/mixed-download/${postId}/`,
        { audio_ids: selectedAudioIds }, // empty array => backend mixes only main
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `mix_post_${postId}.mp3`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error(e);
      toast.error("Download failed");
    } finally {
      setIsMixing(false);
    }
  };

  const refetchPost = async () => {
    if (!postId) return;
    const { data } = await axios.get(`/posts/${postId}/`);
    setPost(data);
  };

  const approveAudio = async (audioId: string) => {
    try {
      setIsModerating(true);
      await axios.patch(`/audiofiles/${audioId}/status/`, { status: 'AP' });
      await refetchPost();
    } catch (e) {
      console.error(e);
      toast.error('Failed to approve audio.');
    } finally {
      setIsModerating(false);
    }
  };

  const submitReject = async () => {
    if (!rejectId) return;
    if (!rejectNote.trim()) {
      toast.error("Please add a rejection note.");
      return;
    }
    try {
      setIsModerating(true);
      await axios.patch(`/audiofiles/${rejectId}/status/`, {
        status: 'RJ',
        review_note: rejectNote.trim(),
      });
      await refetchPost();
      setRejectId(null);
      setRejectNote('');
    } catch (e) {
      console.error(e);
      toast.error('Failed to reject audio.');
    } finally {
      setIsModerating(false);
    }
  };

  const toggleLikeAudio = async (audioId: string) => {
    try {
      const { data } = await axios.post(`/audiofiles/${audioId}/like-toggle/`, {});
      setPost((prev) => {
        if (!prev) return prev;
        const updatedSections = prev.sections.map(sec => ({
          ...sec,
          audio_files: sec.audio_files.map(a =>
            a.id === audioId ? { ...a, is_liked: data.liked, likes_count: data.likes_count } : a
          ),
        }));
        return { ...prev, sections: updatedSections };
      });
    } catch (e) {
      console.error(e);
      toast.error("Failed to like/unlike audio.");
    }
  };

  const deleteAudio = async (audioId: string) => {
    try {
      setDeletingAudioId(audioId);
      await axios.delete(`/audiofiles/${audioId}/`);
      setPost((prev) => {
        if (!prev) return prev;
        const updatedSections = prev.sections.map(sec => ({
          ...sec,
          audio_files: sec.audio_files.filter(a => a.id !== audioId),
        }));
        return { ...prev, sections: updatedSections };
      });

      setTracks((prev) => prev?.filter(t => String(t.id) !== String(audioId)));
      setPlayingTracks((prev) => prev.filter(id => String(id) !== String(audioId)));
      setAudioIdDelete("")
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete audio.");
    } finally {
      setDeletingAudioId(null);
    }
  };

  const fetchComments = async () => {
    if (!postId) return;
    const { data } = await axios.get(`/posts/${postId}/comments/`);
    setComments(data);
  };

  useEffect(() => {
    fetchComments();
  }, [postId]);

  const addComment = async (text: string) => {
    if (!text.trim() || !postId) return;
    try {
      setIsSendingComment(true);
      await axios.post(`/posts/${postId}/comments/`, { text: text.trim() });
      await fetchComments();
    } catch (e) {
      console.error(e);
      toast.error("Failed to add comment.");
    } finally {
      setIsSendingComment(false);
    }
  };

  const deleteComment = async (id: number) => {
    try {
      await axios.delete(`/comments/${id}/`);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete comment.");
    }
  };

  const startEditing = async (audio: AudioFile) => {
    if (openingEditRef.current) return;
    openingEditRef.current = true;
    setOpeningEditId(String(audio.id));
    try {
      const res = await fetch(audio.audio_file);       // get blob from server
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], "audio.wav", { type: blob.type });
      setEditingFile(file);                            // 👈 store it
      setEditingAudio(audio);
    } catch (err) {
      console.error("Failed to load audio for editing:", err);
      toast.error("Couldn't open this audio for editing.", "Please try again.");
    } finally {
      openingEditRef.current = false;
      setOpeningEditId(null);
    }
  };

  const saveEditedAudioDirect = async (file: File) => {
    if (!editingAudio) return;

    setSavingEditedId(String(editingAudio.id));
    const formData = new FormData();
    formData.append("audio_file", file);

    try {
      const { data } = await axios.patch(
        `/audiofiles/${editingAudio.id}/`,
        formData,
        // { headers: { "Content-Type": "multipart/form-data" } }
      );

      setPost(prev =>
        prev
          ? {
            ...prev,
            sections: prev.sections.map(sec => ({
              ...sec,
              audio_files: sec.audio_files.map(a =>
                a.id === editingAudio.id ? data : a
              ),
            })),
          }
          : prev
      );

      setTracks(prev =>
        prev?.map(t =>
          t.id === String(editingAudio.id) ? { ...t, src: data.audio_file } : t
        )
      );

      setEditingAudio(null);
      setEditedBlob(null);
    } catch (err) {
      console.error("Failed to save edited audio:", err);
    } finally {
      setSavingEditedId(null);
    }
  };

  const audioCount = post ? post.sections.reduce((n, s) => n + (s.audio_files?.length ?? 0), 0) : 0;
  const pendingCount = post
    ? post.sections.reduce((n, s) => n + (s.audio_files ?? []).filter((a) => a.status === "PD").length, 0)
    : 0;
  const canDeletePost = post
    ? post.sections.every(sec => !Array.isArray(sec.audio_files) || sec.audio_files.length === 0)
    : false;
  const mixAudios = (post?.sections ?? []).flatMap((s) =>
    (s.audio_files ?? [])
      .filter((a) => playingTracks.includes(String(a.id)))
      .map((a) => ({ name: a.user_info.username, instrument: s.instrument.name }))
  );
  // Only the owner gets the review filters (statuses aren't shown to others).
  const visibleAudios = (files: AudioFile[]) =>
    !isOwner || takeFilter === "all"
      ? files
      : files.filter((a) => (takeFilter === "pending" ? a.status === "PD" : a.status === "AP"));

  const smallOutline = "rounded-full px-3.5 py-1.5 text-sm font-semibold text-primary ring-[1.5px] ring-inset ring-primary hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed";
  const ghostButton = "flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-semibold hover:bg-gray-f6 disabled:opacity-50 disabled:cursor-not-allowed";
  const switchTrack = "relative w-[34px] h-5 rounded-full bg-[#cfd5ca] peer-checked:bg-primary transition-colors shrink-0 after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-3.5 peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-1";

  const renderAudio = (audio: AudioFile) => {
    const id = String(audio.id);
    const isMine = authData?.id != null && String(authData.id) === String(audio.user);
    const showStatus = isOwner || isMine;
    const inMix = playingTracks.includes(id);
    return (
      <div key={audio.id} className="px-4 768:px-5 py-3.5 border-t border-gray-e8 first:border-t-0 flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link to={`/profile/${audio.user}`} className="flex items-center gap-2.5 min-w-0 group">
            <img
              src={audio.user_info.profile_photo || "/Images/UserProfile.png"}
              alt=""
              className="w-8 h-8 border rounded-full object-cover shrink-0"
            />
            <span className="flex flex-col min-w-0">
              <span className="text-[15px] font-semibold truncate group-hover:underline">{audio.user_info.username}</span>
              <span className="text-xs text-gray-70" title={formatDMYHM(audio.created_at)}>Added {formatTimeAgo(audio.created_at)}</span>
            </span>
          </Link>
          {showStatus && (
            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${statusBadgeClass(audio.status)}`}>
              {audio.status === "PD" && isOwner ? "Waiting for review" : statusLabel(audio.status)}
            </span>
          )}
        </div>

        <div className="flex flex-wrap 888:flex-nowrap items-center gap-x-3 gap-y-2">
          {/* "In mix" sits on the left, before the player. */}
          <label
            className="flex items-center gap-2 text-[13px] font-semibold cursor-pointer select-none shrink-0"
            title="Include this audio when you play together or download the mix"
          >
            <input
              type="checkbox"
              role="switch"
              className="peer sr-only"
              checked={inMix}
              onChange={(e) => handleCheckboxChange(e, id)}
              aria-label={`In mix: ${audio.user_info.username}`}
            />
            <span className={switchTrack} aria-hidden="true" />
            <span className={inMix ? "text-primary" : "text-black41"}>In mix</span>
          </label>

          <AudioPlayer
            preload="auto"
            ref={tracks?.find((t) => t.id === id)?.audioRef}
            src={audio.audio_file}
            waveformSeed={id}
            className="flex-1 min-w-[220px]"
          />

          <div className="flex items-center gap-1 shrink-0">
            {audio.status === "AP" && (
              <button
                type="button"
                className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold disabled:cursor-not-allowed ${audio.is_liked ? "bg-red-primary/10 text-red-primary" : "bg-gray-f6 text-black41"}`}
                disabled={!authData?.id}
                aria-pressed={audio.is_liked}
                aria-label={`${audio.is_liked ? "Unlike" : "Like"} (${audio.likes_count} likes)`}
                title={authData?.id ? undefined : "Log in to like this audio"}
                onClick={() => toggleLikeAudio(audio.id)}
              >
                {audio.is_liked ? <PiHeartFill /> : <PiHeart />}
                <span className="tabular-nums">{audio.likes_count}</span>
              </button>
            )}
            {isMine && audio.status === "PD" && (
              <button
                type="button"
                className={`${ghostButton} text-black41`}
                onClick={() => startEditing(audio)}
                disabled={isModerating || openingEditId !== null}
                aria-busy={openingEditId === String(audio.id)}
              >
                {openingEditId === String(audio.id) ? (
                  <>
                    <CircularLoading size={16} color="currentColor" /> Opening…
                  </>
                ) : (
                  <>
                    <CiEdit className="text-lg" /> Edit
                  </>
                )}
              </button>
            )}
            {isMine && (
              <button
                type="button"
                className={`${ghostButton} text-red-primary hover:!bg-red-primary/[0.08]`}
                onClick={() => {
                  setAudioIdDelete(audio.id);
                  setShowDeleteAudioModal(true);
                }}
              >
                <CiTrash className="text-lg" /> Delete
              </button>
            )}
          </div>
        </div>

        {isOwner && audio.status === "PD" && (
          <div className="flex flex-wrap items-center gap-2 bg-[#fff8ec] rounded-xl px-3 py-2">
            <span className="mr-auto flex items-center gap-1.5 text-[13px] font-semibold text-[#8a4f00]">
              <IoAlertCircleOutline className="text-base" /> New audio collaboration, waiting for your review
            </span>
            <button
              type="button"
              onClick={() => setApproveId(audio.id)}
              disabled={isModerating}
              className="flex items-center gap-1 rounded-full bg-primary text-white px-3.5 py-1.5 text-sm font-semibold hover:bg-[#526554] disabled:opacity-50"
            >
              <PiCheckBold /> Approve
            </button>
            <button
              type="button"
              onClick={() => setRejectId(audio.id)}
              disabled={isModerating}
              className="rounded-full px-3.5 py-1.5 text-sm font-semibold text-red-primary ring-[1.5px] ring-inset ring-red-primary hover:bg-red-primary/5 disabled:opacity-50"
            >
              Reject…
            </button>
          </div>
        )}

        {showStatus && audio.status === "RJ" && audio.review_note && (
          <div className="rounded-xl border border-[#f4cccc] bg-[#fff6f6] px-3 py-2 text-sm text-[#7a1b1b]">
            <span className="block text-[11px] font-bold uppercase tracking-wide text-red-primary">Why it was rejected</span>
            {audio.review_note}
          </div>
        )}

        {showStatus && (audio.status === "AP" || audio.status === "RJ") && audio.reviewed_at && (
          <span className="text-xs text-gray-70">Reviewed {formatDMYHM(audio.reviewed_at)}</span>
        )}
      </div>
    );
  };

  const renderAddPart = (section: Section) => {
    const step = selectedFile ? 1 : 0;
    const steps = ["Record or upload", "Edit and align", "Submit"];
    const meterBars = 15;
    const lit = Math.round(inputLevel * meterBars);
    return (
      <div className="border-t border-gray-e8 bg-[#fbfcfa] px-4 768:px-5 py-5 flex flex-col gap-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-lg font-bold">Add audio for {section.instrument.name}</h3>
          {selectedFile && (
            <button type="button" onClick={backToRecordOrUpload} className="text-sm font-semibold text-primary underline underline-offset-[3px]">
              Choose a different audio file
            </button>
          )}
        </div>

        <ol className="flex flex-wrap gap-2" aria-label="Steps">
          {steps.map((label, i) => {
            const state = i < step ? "done" : i === step ? "current" : "todo";
            return (
              <li
                key={label}
                aria-current={state === "current" ? "step" : undefined}
                className={`flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1 text-sm font-semibold ${
                  state === "current" ? "bg-primary text-white" : state === "done" ? "bg-primary/[0.07] text-primary" : "bg-gray-f6 text-gray-70"
                }`}
              >
                <span className={`w-[22px] h-[22px] rounded-full grid place-items-center text-xs ${state === "current" ? "bg-white text-primary" : "bg-white border border-gray-e8"}`}>
                  {state === "done" ? <PiCheckBold /> : i + 1}
                </span>
                {label}
              </li>
            );
          })}
        </ol>

        {!selectedFile ? (
          <>
            <div className="grid 768:grid-cols-[1.2fr_1fr] gap-3.5">
              <div className="relative bg-white border border-gray-e8 rounded-[20px] p-4 flex flex-col gap-3">
                {countdown !== null && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 bg-black/55 rounded-[20px]" role="status">
                    <span className="text-white text-6xl font-bold animate-pulse">{countdown}</span>
                    <span className="text-white/80 text-sm">Recording starts after the count-in</span>
                  </div>
                )}
                <span className="font-semibold">Record</span>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecordingWithCountdown}
                    disabled={countdown !== null}
                    aria-label={isRecording ? "Stop recording" : "Start recording"}
                    className="w-16 h-16 shrink-0 rounded-full bg-[#e0312b] grid place-items-center shadow-[0_0_0_6px_rgba(224,49,43,0.13)] hover:bg-[#c92a25] disabled:opacity-60"
                  >
                    <span className={`block w-5 h-5 bg-white ${isRecording ? "rounded-[4px]" : "rounded-full"}`} />
                  </button>
                  <div>
                    <div className="text-2xl font-bold tabular-nums">{formatRecordingTime(time)}</div>
                    <span className="text-[13px] text-gray-70">
                      {isRecording
                        ? "Recording. Press to stop."
                        : countInSeconds
                          ? `Starts after a ${countInSeconds}-second count-in`
                          : "Starts right away"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <span className="text-[13px] text-gray-70 w-[78px] shrink-0">Input level</span>
                  <div className="flex items-end gap-[2px] h-[22px]" aria-hidden="true">
                    {Array.from({ length: meterBars }, (_, i) => (
                      <span
                        key={i}
                        className={`block w-[5px] rounded-sm ${i < lit ? (i >= meterBars - 2 ? "bg-[#d64a2b]" : i >= meterBars - 5 ? "bg-[#e0a32b]" : "bg-[#7fa66f]") : "bg-gray-e8"}`}
                        style={{ height: `${6 + (i / (meterBars - 1)) * 16}px` }}
                      />
                    ))}
                  </div>
                  {!isRecording && <span className="text-xs text-gray-70">Shows while recording</span>}
                </div>

                <label className="flex flex-col gap-1 text-[13px]">
                  <span className="font-semibold">Microphone</span>
                  <select
                    value={micId}
                    onChange={(e) => setMicId(e.target.value)}
                    disabled={isRecording || countdown !== null}
                    className="border border-[#D9D9D9] rounded-full px-3.5 py-2 bg-white text-sm"
                  >
                    <option value="">Default microphone</option>
                    {micDevices
                      .filter((d) => d.deviceId && d.deviceId !== "default" && d.deviceId !== "communications")
                      .map((d, i) => (
                        <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>
                      ))}
                  </select>
                </label>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <label className="flex items-center gap-2 text-[13px] font-semibold cursor-pointer select-none">
                    <input
                      type="checkbox"
                      role="switch"
                      className="peer sr-only"
                      checked={monitorMainWhileRecording}
                      onChange={(e) => setMonitorMainWhileRecording(e.target.checked)}
                    />
                    <span className={switchTrack} aria-hidden="true" />
                    <span className={monitorMainWhileRecording ? "text-primary" : "text-black41"}>Play the main track while recording</span>
                  </label>
                  <span className="text-xs text-gray-70">Use headphones so it isn't recorded too.</span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 text-[13px]" role="group" aria-label="Count-in">
                  <span className="text-gray-70 mr-1">Count-in</span>
                  {([0, 3, 5] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setCountInSeconds(s)}
                      aria-pressed={countInSeconds === s}
                      disabled={isRecording || countdown !== null}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${countInSeconds === s ? "bg-primary text-white" : "bg-gray-f6 text-black41 hover:bg-gray-e8"}`}
                    >
                      {s === 0 ? "Off" : `${s} s`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <span className="font-semibold">Or upload an audio file</span>
                <FileDropzone
                  accept="audio/*"
                  inputLabel={`Upload audio for ${section.instrument.name}`}
                  isAcceptedFile={isAudioFile}
                  rejectMessage={(file) => `"${file.name}" isn't an audio file. Choose an audio file such as MP3 or WAV.`}
                  onFile={selectTakeFile}
                  hasFile={false}
                  emptyTitle="Drag an audio file here, or"
                  emptyHint="Any audio file, such as MP3 or WAV."
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type={ButtonTypes.CANCEL} htmlType="button" onClick={closeAddPart} className="!px-4 !py-2 font-semibold" disabled={isUploadingAudio}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <AudioEditor
            file={selectedFile}
            audioContext={audioContext.current!}
            onAudioEdited={(editedFile) => {
              handleAddAudio(editedFile, section.id);
            }}
            onSubmitOriginal={() => handleAddAudio(selectedFile, section.id)}
            onCancel={closeAddPart}
            mainTrackUrl={post?.mp3_file || undefined}
            sectionName={section.instrument.name}
            mainAudioCurrentTime={currentTime}
            passEditedAudioRef={(ref) => {
              editedWaveSurferRef.current = ref;
            }}
            handlePlayTogether={handlePlayTogether}
            pauseMain={pauseMainTrack}
            reset={resetPlayingTogether}
            isSaving={isUploadingAudio}
          />
        )}
      </div>
    );
  };

  return (
    <MainContainer>
      {isLoading ?
        <div className="w-full h-screen flex justify-center items-center">
          <CircularLoading />
        </div>
        : post ? (
          <div className="flex flex-col gap-7 my-10">
            <Link to="/projects" className="flex items-center gap-1.5 w-fit text-sm font-semibold text-primary hover:underline">
              <BsArrowLeft /> Projects
            </Link>

            <section className="grid 992:grid-cols-[1.25fr_1fr] gap-6 bg-white border border-gray-e8 rounded-3xl shadow-md p-5 768:p-6">
              <div className={`flex flex-col gap-4 min-w-0 ${post.pdf_file ? "" : "992:col-span-2"}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex flex-col gap-2 min-w-0">
                    <h1 className="text-3xl 768:text-[2rem] font-bold leading-tight break-words">{post.title}</h1>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-black41">
                      {post.user_info && (
                        <Link to={`/profile/${post.user}`} className="flex items-center gap-2 font-semibold text-gray-800 hover:underline">
                          <img
                            src={post.user_info.profile_photo || "/Images/UserProfile.png"}
                            alt=""
                            className="w-8 h-8 border rounded-full object-cover"
                          />
                          {post.user_info.username}
                        </Link>
                      )}
                      <span aria-hidden="true" className="w-1 h-1 rounded-full bg-gray-bd" />
                      <span title={formatDMYHM(post.created_at)}>Created {formatDMY(post.created_at)}</span>
                      <span aria-hidden="true" className="w-1 h-1 rounded-full bg-gray-bd" />
                      {post.is_archived ? (
                        <span className="text-xs font-bold uppercase tracking-wide bg-gray-200 rounded-full px-2.5 py-0.5" title={`Archived at ${formatDMYHM(post.archived_at)}`}>
                          Archived {formatDMY(post.archived_at)}
                        </span>
                      ) : (
                        <span className="text-xs font-bold bg-green-primary33 text-[#03644a] rounded-full px-2.5 py-0.5">Open for collaboration</span>
                      )}
                    </div>
                  </div>

                  {isOwner && (
                    <div className="flex items-center gap-2" ref={ownerMenuRef}>
                      <button
                        type="button"
                        onClick={() => navigate(`/post/${postId}`)}
                        disabled={post.is_archived}
                        title={post.is_archived ? "Unarchive the project to edit it" : undefined}
                        className={`${smallOutline} flex items-center gap-1.5`}
                      >
                        <CiEdit className="text-lg" /> Edit project
                      </button>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setOwnerMenuOpen((open) => !open)}
                          aria-haspopup="menu"
                          aria-expanded={ownerMenuOpen}
                          aria-label="More project actions"
                          className="w-9 h-9 rounded-full grid place-items-center text-xl text-primary ring-[1.5px] ring-inset ring-primary hover:bg-primary/5"
                        >
                          <IoEllipsisHorizontal />
                        </button>
                        {ownerMenuOpen && (
                          <div role="menu" className="absolute right-0 top-11 z-20 w-64 bg-white border border-gray-e8 rounded-2xl shadow-lg p-1.5">
                            <button
                              type="button"
                              role="menuitem"
                              disabled={isArchiving}
                              onClick={() => {
                                setOwnerMenuOpen(false);
                                if (post.is_archived) archivePostHandler();
                                else setShowArchiveModal(true);
                              }}
                              className="w-full text-left rounded-xl px-3 py-2 hover:bg-gray-f6 disabled:opacity-50"
                            >
                              <span className="block text-sm font-semibold">{post.is_archived ? "Unarchive project" : "Archive project"}</span>
                              <span className="block text-xs text-gray-70">{post.is_archived ? "Opens it for audio collaborations again." : "Stops new audio collaborations. You can undo it."}</span>
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              disabled={!canDeletePost}
                              onClick={() => {
                                setOwnerMenuOpen(false);
                                setShowDeleteModal(true);
                              }}
                              className="w-full text-left rounded-xl px-3 py-2 hover:bg-gray-f6 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                            >
                              <span className={`block text-sm font-semibold ${canDeletePost ? "text-red-primary" : "text-gray-bd"}`}>Delete project</span>
                              <span className="block text-xs text-gray-70">{canDeletePost ? "This can't be undone." : "Only possible while it has no audio collaborations."}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {post.instruments.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 text-[13px] text-black41">
                    Looking for
                    {post.instruments.map((instrument) => (
                      <span key={instrument.id} className="text-xs font-semibold text-primary bg-primary/[0.07] px-2.5 py-0.5 rounded-full">
                        {instrument.name}
                      </span>
                    ))}
                  </div>
                )}

                <p className="whitespace-pre-line max-w-[65ch]">{post.description}</p>

                <div className="mt-auto flex flex-col gap-2">
                  <span className="text-[13px] font-bold tracking-[0.06em] uppercase text-[#526554]">Main track</span>
                  <AudioPlayer
                    className="w-full"
                    size="lg"
                    waveformSeed={String(post.id)}
                    ref={mainAudioRef}
                    onPlay={() => { setIsMainPlaying(true); handleMainAudioPlay(); }}
                    onPause={() => { setIsMainPlaying(false); handleMainAudioPause(); }}
                    onTimeUpdate={() => setCurrentTime(mainAudioRef.current?.currentTime ?? 0)}
                    onSeeked={() => { setCurrentTime(mainAudioRef.current?.currentTime ?? 0); resyncAudiosToMain(); }}
                  >
                    <source src={post.mp3_file} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </AudioPlayer>
                </div>
              </div>

              {post.pdf_file &&
                <div className="flex flex-col gap-2 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-bold tracking-[0.06em] uppercase text-[#526554]">Sheet music</span>
                    <a href={post.pdf_file} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[13px] font-semibold text-primary underline underline-offset-[3px]">
                      Open full screen <IoOpenOutline />
                    </a>
                  </div>
                  <iframe
                    title="Sheet music"
                    src={post.pdf_file}
                    className="w-full h-[420px] rounded-2xl border border-gray-e8 bg-gray-f6"
                  />
                </div>
              }
            </section>

            {post.sections.length === 0 && isOwner && (
              <p className="text-sm text-black41 bg-gray-f6 rounded-2xl px-4 py-3">
                Musicians can't add audio yet. Choose the instruments you need in <Link to={`/post/${postId}`} className="font-semibold text-primary underline underline-offset-[3px]">Edit project</Link>.
              </p>
            )}

            {post.sections.length > 0 &&
              <section className="flex flex-col gap-3.5" aria-labelledby="collaboration-heading">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <h2 id="collaboration-heading" className="text-2xl font-semibold">Collaboration section</h2>
                  {isOwner && audioCount > 0 && (
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Show audio collaborations">
                      {([
                        ["all", "All"],
                        ["pending", `Waiting for review${pendingCount ? ` (${pendingCount})` : ""}`],
                        ["approved", "Approved"],
                      ] as const).map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setTakeFilter(value)}
                          aria-pressed={takeFilter === value}
                          className={`text-[13px] font-semibold px-3 py-1.5 rounded-full ${takeFilter === value ? "bg-primary text-white" : "bg-gray-f6 text-black41 hover:bg-gray-e8"}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {post.sections.map((section) => {
                  const files = section.audio_files ?? [];
                  const shown = visibleAudios(files);
                  return (
                    <div key={section.id} className="bg-white border border-gray-e8 rounded-[22px] overflow-hidden">
                      <div className="flex flex-wrap items-center justify-between gap-3 px-4 768:px-5 py-3 bg-secondary">
                        <div className="flex items-center gap-2.5">
                          <h3 className="text-[17px] font-semibold">{section.instrument.name}</h3>
                          <span className="text-xs font-bold text-primary bg-white/70 rounded-full px-2.5 py-0.5">
                            {files.length} {files.length === 1 ? "audio" : "audios"}
                          </span>
                        </div>
                        {(authData?.id && !post.is_archived) ? (
                          currentSectionId === section.id ? (
                            <button type="button" onClick={closeAddPart} className="flex items-center gap-1 text-sm font-semibold text-black41 hover:underline">
                              <IoClose /> Close
                            </button>
                          ) : (
                            <Button
                              type={ButtonTypes.PRIMARY}
                              htmlType="button"
                              onClick={() => openAddPart(section.id)}
                              className="!px-4 !py-2 flex items-center gap-1 text-sm font-semibold"
                              disabled={isUploadingAudio}
                            >
                              <IoAdd className="text-base" /> Add audio
                            </Button>
                          )
                        ) : (
                          <></>
                        )}
                      </div>

                      {currentSectionId === section.id && renderAddPart(section)}

                      {shown.length > 0 ? (
                        shown.map(renderAudio)
                      ) : (
                        <p className="px-4 768:px-5 py-4 text-gray-70">
                          {files.length === 0
                            ? `No audios added yet for ${section.instrument.name}.`
                            : "No audios match this filter."}
                        </p>
                      )}
                    </div>
                  );
                })}

                {audioCount > 0 && (
                  <div className="sticky bottom-4 z-10 flex flex-wrap items-center gap-3 bg-primary text-white rounded-2xl pl-5 pr-3 py-2.5 shadow-lg">
                    <div className="flex flex-col mr-auto min-w-0">
                      <b className="text-[15px]">
                        In the mix: main track{mixAudios.length ? ` + ${mixAudios.length} ${mixAudios.length === 1 ? "audio" : "audios"}` : " only"}
                      </b>
                      <span className="text-[13px] text-white/80 truncate">
                        {mixAudios.length
                          ? mixAudios.map((t) => `${t.name} (${t.instrument})`).join(", ")
                          : "Turn on \"In mix\" next to an audio to add it."}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={toggleMainPlayback}
                      className="flex items-center gap-1.5 rounded-full bg-white/[0.12] hover:bg-white/20 px-4 py-2 text-sm font-semibold"
                    >
                      {isMainPlaying ? <CiPause1 className="text-lg" /> : <CiPlay1 className="text-lg" />}
                      {isMainPlaying ? "Pause" : "Play together"}
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadOutput}
                      disabled={isMixing}
                      className="flex items-center gap-1.5 rounded-full bg-white text-primary hover:bg-white/90 px-4 py-2 text-sm font-semibold disabled:opacity-70"
                    >
                      <CiSaveDown2 className="text-lg" />
                      {isMixing ? "Preparing…" : "Download mix"}
                    </button>
                  </div>
                )}
              </section>
            }

            <CommentSection
              comments={comments}
              onSubmit={addComment}
              onDelete={deleteComment}
              isSubmitting={isSendingComment}
              ownerId={post.user}
            />

            {/* Rendered once (it used to be rendered inside every one of
                your own audios, so editing could open several stacked
                editors). */}
            {editingAudio && (
              <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-label="Edit audio"
                  className="bg-white w-full max-w-[1000px] max-h-[92vh] overflow-y-auto rounded-3xl shadow-lg p-4 768:p-6"
                >
                  <div className="flex justify-between items-center gap-3">
                    <h2 className="text-lg font-semibold">Edit audio</h2>
                    <button
                      type="button"
                      onClick={() => setEditingAudio(null)}
                      disabled={savingEditedId === String(editingAudio.id)}
                      aria-label="Close"
                      className="w-9 h-9 rounded-full grid place-items-center text-2xl text-black41 hover:bg-gray-f6 disabled:opacity-50"
                    >
                      <IoClose />
                    </button>
                  </div>
                  <AudioEditor
                    file={editingFile}
                    audioContext={audioContext.current!}
                    onAudioEdited={async (editedFile) => {
                      await saveEditedAudioDirect(editedFile);
                    }}
                    onCancel={() => setEditingAudio(null)}
                    submitLabel="Save changes"
                    mainTrackUrl={post.mp3_file || undefined}
                    sectionName={post.sections.find((s) => (s.audio_files ?? []).some((a) => a.id === editingAudio.id))?.instrument.name}
                    mainAudioCurrentTime={currentTime}
                    passEditedAudioRef={(ref) => {
                      editedWaveSurferRef.current = ref;
                    }}
                    handlePlayTogether={handlePlayTogether}
                    pauseMain={pauseMainTrack}
                    reset={resetPlayingTogether}
                    isSaving={savingEditedId === String(editingAudio.id)}
                  />
                </div>
              </div>
            )}

            {showArchiveModal && (
              <LastConfirmWarningModal
                onCancel={() => setShowArchiveModal(false)}
                onConfirm={() => {
                  archivePostHandler();
                  setShowArchiveModal(false)
                }}
                onBackdrop={() => setShowArchiveModal(false)}
                title="Archive project"
                message="Are you sure you want to archive this project?"
                confirmButton="Yes, archive"
              // confirmDisabled={isArchiving}
              />
            )}

            {showDeleteModal && (
              <LastConfirmWarningModal
                onCancel={() => setShowDeleteModal(false)}
                onConfirm={() => {
                  deletePostHandler();
                  setShowDeleteModal(false)
                }}
                onBackdrop={() => setShowDeleteModal(false)}
                title="Delete project"
                message="Are you sure you want to delete this project?"
                confirmButton="Yes, delete"
              />
            )}

            {showDeleteAudioModal && (
              <LastConfirmWarningModal
                onCancel={() => setShowDeleteAudioModal(false)}
                onConfirm={() => {
                  deleteAudio(audioIdDelete)
                  setShowDeleteAudioModal(false)
                }}
                onBackdrop={() => setShowDeleteAudioModal(false)}
                title="Delete audio"
                message="Are you sure you want to delete this audio?"
                confirmButton="Yes, delete"
              />
            )}

            {approveId !== null && (
              <LastConfirmWarningModal
                onCancel={() => setApproveId(null)}
                onConfirm={() => {
                  approveAudio(approveId);
                  setApproveId(null);
                }}
                onBackdrop={() => setApproveId(null)}
                title="Approve audio"
                message="Are you sure you want to approve this audio contribution? It will become publicly visible."
                confirmButton="Yes, approve"
                confirmDisabled={isModerating}
              />
            )}

            {rejectId !== null && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-2xl w-[90%] max-w-md" role="dialog" aria-modal="true" aria-label="Reject audio">
                  <h3 className="text-lg font-semibold mb-2">Reject audio</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Add a short note explaining why you’re rejecting this audio.
                  </p>
                  <textarea
                    className="w-full border rounded-lg p-2 h-28"
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    placeholder="Reason for rejection…"
                    aria-label="Reason for rejection"
                  />
                  <div className="mt-4 flex justify-end gap-2">
                    <Button
                      type={ButtonTypes.CANCEL}
                      onClick={() => {
                        setRejectId(null);
                        setRejectNote('');
                      }}
                      className="!px-4 !py-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      type={ButtonTypes.PRIMARY}
                      onClick={submitReject}
                      className="!px-4 !py-2"
                      disabled={isModerating || !rejectNote.trim()}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full min-h-[400px] my-10 bg-secondary p-6 rounded-3xl flex flex-col items-center justify-center gap-2 text-center">
            <p className="font-semibold text-lg">This project isn't available.</p>
            <p className="text-black41">It may have been deleted, or the link is wrong.</p>
            <Link to="/projects" className="mt-1 font-semibold text-primary underline underline-offset-[3px]">Back to projects</Link>
          </div>
        )}
    </MainContainer>
  );
};

export default PostPage;
